// NodeActivityService.js

/**
 * NodeActivityService.js
 * 
 * Service for recording and subscribing to node activity events.
 */

// Firebase
import { collection, doc, limit, onSnapshot, orderBy, query, serverTimestamp, setDoc, Timestamp } from 'firebase/firestore';
import { db, collections } from 'firebaseConfig';

// Utilities
// Limit for number of events to fetch if not specified
export const NODE_ACTIVITY_EVENTS_LIMIT_DEFAULT = 25;

// Event kinds
export const NodeActivityEventKind = Object.freeze({
  FIELD_CHANGED: 'field-changed',
  CHECKLIST_CHANGED: 'checklist-changed',
});

// Constants for activity categories and types
const ACTIVITY_CATEGORY_CONTENT = 'content';
const ACTIVITY_TYPE_FIELD_CHANGED = 'node_field_changed';
const ACTIVITY_TYPE_CHECKLIST_CHANGED = 'node_checklist_changed';

const toSentenceCase = (value) => {
  const normalized = String(value || '').replace(/[_-]/g, ' ').trim();
  if (!normalized) return '';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const buildFieldLabelFromPath = (fieldPath) => {
  const normalizedPath = String(fieldPath || '').trim();
  if (!normalizedPath) return 'Field';

  const segments = normalizedPath.split('.');
  const lastSegment = segments[segments.length - 1] || normalizedPath;
  return toSentenceCase(lastSegment) || 'Field';
};

const sanitizeFieldChangeValue = (value) => {
  if (value === undefined) return null;
  return value ?? null;
};

const buildFieldChangedPromptHints = (changes = [], actorName = null) => {
  const safeActorName = String(actorName || '').trim() || 'Someone';

  return changes
    .map((change) => String(change?.fieldLabel || '').trim())
    .filter(Boolean)
    .map((fieldLabel) => `${safeActorName} updated ${fieldLabel}.`);
};

const buildFieldChangedSummaries = (changes = []) => {
  return changes
    .map((change) => String(change?.fieldLabel || '').trim())
    .filter(Boolean)
    .map((fieldLabel) => `Updated ${fieldLabel}.`);
};

const normalizeFieldChanges = (changes = []) => {
  return (Array.isArray(changes) ? changes : [])
    .map((change) => {
      const fieldPath = String(change?.fieldPath || change?.field || '').trim();
      if (!fieldPath) return null;

      const fieldLabel = String(change?.fieldLabel || '').trim() || buildFieldLabelFromPath(fieldPath);

      return {
        changeKind: 'field',
        fieldPath,
        fieldLabel,
        previousValue: sanitizeFieldChangeValue(change?.previousValue),
        newValue: sanitizeFieldChangeValue(change?.newValue),
      };
    })
    .filter(Boolean);
};

const buildFieldChangedDoc = (event, actor) => {
  const nodeKey = String(event?.nodeKey || '').trim();
  if (!nodeKey) return null;

  const changes = normalizeFieldChanges(event?.changes);
  if (!changes.length) return null;

  const actorKey = event?.actorKey
    || actor?.key
    || actor?.uid
    || actor?.data_profile?.key
    || null;
  const actorName = event?.actorName
    || actor?.name
    || actor?.displayName
    || actor?.data_profile?.name
    || event?.actor?.name
    || null;
  const occurredAt = toTimestamp(event?.occurredAt);
  const promptHints = buildFieldChangedPromptHints(changes, actorName);
  const changeSummaries = buildFieldChangedSummaries(changes);

  return {
    nodeKey,
    parentNodeKey: nodeKey,
    baseKey: event?.baseKey || null,
    actorKey,
    actorName,
    actorNodeKey: actorKey,
    category: ACTIVITY_CATEGORY_CONTENT,
    type: ACTIVITY_TYPE_FIELD_CHANGED,
    kind: NodeActivityEventKind.FIELD_CHANGED,
    occurredAt,
    timestamp: serverTimestamp(),
    dateCreated: serverTimestamp(),
    promptHints,
    changeSummaries,
    changes,
    processed: {
      summarizedAt: null,
      notificationAt: null,
    },
    raw: event?.raw && typeof event.raw === 'object' ? event.raw : {},
  };
};

const normalizeChecklistChanges = (changes = []) => {
  return (Array.isArray(changes) ? changes : [])
    .map((change) => {
      const changeKind = String(change?.changeKind || '').trim();
      const checklistTitle = String(change?.checklistTitle || '').trim();
      const itemText = String(change?.itemText || '').trim();

      if (!changeKind) return null;

      return {
        changeKind,
        checklistKey: String(change?.checklistKey || '').trim() || null,
        fieldName: String(change?.fieldName || '').trim() || null,
        checklistTitle: checklistTitle || null,
        itemKey: String(change?.itemKey || '').trim() || null,
        itemText: itemText || null,
        previousValue: sanitizeFieldChangeValue(change?.previousValue),
        newValue: sanitizeFieldChangeValue(change?.newValue),
      };
    })
    .filter(Boolean);
};

const formatChecklistChangeSummary = (change) => {
  const checklistTitle = String(change?.checklistTitle || '').trim() || 'Checklist';
  const itemText = String(change?.itemText || '').trim();

  switch (change?.changeKind) {
    case 'checklist_created':
      return `Created ${checklistTitle}.`;
    case 'checklist_deleted':
      return `Deleted ${checklistTitle}.`;
    case 'checklist_renamed':
      return `Renamed ${checklistTitle}.`;
    case 'item_added':
      return itemText ? `Added checklist item "${itemText}".` : `Added an item to ${checklistTitle}.`;
    case 'item_removed':
      return itemText ? `Removed checklist item "${itemText}".` : `Removed an item from ${checklistTitle}.`;
    case 'item_completed':
      return itemText ? `Completed checklist item "${itemText}".` : `Completed an item in ${checklistTitle}.`;
    case 'item_reopened':
      return itemText ? `Reopened checklist item "${itemText}".` : `Reopened an item in ${checklistTitle}.`;
    case 'assignees_changed':
      return itemText ? `Updated assignees for "${itemText}".` : `Updated checklist assignees in ${checklistTitle}.`;
    case 'items_reordered':
      return `Reordered items in ${checklistTitle}.`;
    case 'item_text_changed':
      return itemText ? `Updated checklist item "${itemText}".` : `Updated a checklist item in ${checklistTitle}.`;
    default:
      return `Updated ${checklistTitle}.`;
  }
};

const buildChecklistChangedDoc = (event, actor) => {
  const nodeKey = String(event?.nodeKey || '').trim();
  if (!nodeKey) return null;

  const changes = normalizeChecklistChanges(event?.changes);
  if (!changes.length) return null;

  const actorKey = event?.actorKey
    || actor?.key
    || actor?.uid
    || actor?.data_profile?.key
    || null;
  const actorName = event?.actorName
    || actor?.name
    || actor?.displayName
    || actor?.data_profile?.name
    || event?.actor?.name
    || null;
  const occurredAt = toTimestamp(event?.occurredAt);
  const changeSummaries = changes.map((change) => formatChecklistChangeSummary(change));
  const safeActorName = String(actorName || '').trim() || 'Someone';
  const promptHints = changeSummaries.map((summary) => `${safeActorName} ${summary.charAt(0).toLowerCase()}${summary.slice(1)}`);

  return {
    nodeKey,
    parentNodeKey: nodeKey,
    baseKey: event?.baseKey || null,
    actorKey,
    actorName,
    actorNodeKey: actorKey,
    category: ACTIVITY_CATEGORY_CONTENT,
    type: ACTIVITY_TYPE_CHECKLIST_CHANGED,
    kind: NodeActivityEventKind.CHECKLIST_CHANGED,
    occurredAt,
    timestamp: serverTimestamp(),
    dateCreated: serverTimestamp(),
    promptHints,
    changeSummaries,
    changes,
    processed: {
      summarizedAt: null,
      notificationAt: null,
    },
    raw: event?.raw && typeof event.raw === 'object' ? event.raw : {},
  };
};

/**
 * Converts a value to a Firestore Timestamp.
 * 
 * @param {number|any} value - The value to convert.
 * 
 * @returns {Timestamp} - Firestore Timestamp.
 */
const toTimestamp = (value) => {

  // Check for numeric value
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Timestamp.fromMillis(value);
  }

  // Default to current timestamp
  return serverTimestamp();
};

/**
 * Records node activity events.
 * 
 * @param {Array<object>} events - Array of event objects to record.
 * @param {object|null} actor - The actor node object (optional).
 * 
 * @returns {Promise<void>} - Promise that resolves when recording is complete.
 */
export const recordNodeActivityEvents = async (events = [], actor = null) => {

  // Validate events
  if (!Array.isArray(events) || events.length === 0) return;

  // Array to hold write promises
  const writes = [];

  // Process each event
  for (const event of events) {

    // Initialize document data
    let docData = null;

    // Build document data based on event kind
    if (event.kind === NodeActivityEventKind.FIELD_CHANGED) {
      docData = buildFieldChangedDoc(event, actor);
    }

    if (event.kind === NodeActivityEventKind.CHECKLIST_CHANGED) {
      docData = buildChecklistChangedDoc(event, actor);
    }

    // Skip if no document data
    if (!docData || !docData.nodeKey) {
      continue;
    }

    // Write to node-local subcollection: _nodes/{nodeKey}/events/{eventId}
    const eventsRef = collection(db, collections.nodes, docData.nodeKey, 'events');
    const docRef = doc(eventsRef);
    writes.push(setDoc(docRef, { ...docData, key: docRef.id }));
  }

  // Await all writes
  if (writes.length > 0) {

    // Use Promise.allSettled to handle individual write failures
    const results = await Promise.allSettled(writes);

    // Log any failures
    results.forEach((result) => {
      if (result.status === 'rejected') {
        console.warn('Failed to record node activity event:', result.reason);
      }
    });
  }
};

/**
 * Subscribes to the most recent activity events for a node.
 * Returns the unsubscribe function.
 * 
 * @param {object} options - Subscription options.
 * @param {string} options.nodeKey - The node key to subscribe to.
 * @param {number} [options.limit] - Maximum number of events to fetch.
 * @param {function(Array<object>)} onUpdate - Callback for updates with array of event objects.
 * @param {function(Error)} [onError] - Optional callback for errors.
 * 
 * @returns {function()} - Unsubscribe function.
 */
export const subscribeToNodeActivityEvents = (options, onUpdate, onError) => {

  // Extract options
  const nodeKey = options?.nodeKey;

  // Determine max events
  const maxEvents = Number(options?.limit || options?.pageSize || NODE_ACTIVITY_EVENTS_LIMIT_DEFAULT);
  
  // Validation
  if (!nodeKey) {
    throw new Error('subscribeToNodeActivityEvents: nodeKey is required');
  }

  // Validate onUpdate callback
  if (typeof onUpdate !== 'function') {
    throw new Error('subscribeToNodeActivityEvents: onUpdate callback is required');
  }

  // Determine safe limit
  const safeLimit = Number.isFinite(maxEvents) && maxEvents > 0 ? maxEvents : NODE_ACTIVITY_EVENTS_LIMIT_DEFAULT;
  
  // Reference to events subcollection
  const eventsRef = collection(db, collections.nodes, nodeKey, 'events');
  
  // Build query
  const eventsQuery = query(eventsRef, orderBy('timestamp', 'desc'), limit(safeLimit));

  // Set up real-time listener
  return onSnapshot(
    eventsQuery,
    (snapshot) => {
      const events = snapshot.docs.map((docSnapshot) => ({
        key: docSnapshot.id,
        ...docSnapshot.data(),
      }));
      onUpdate(events);
    },
    (err) => {
      if (typeof onError === 'function') {
        onError(err);
      } else {
        console.warn('[NodeActivityService] subscribeToNodeActivityEvents error:', err);
      }
    }
  );
};
